import format from 'date-fns/format';
import isValidDate from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import { OPERATOR_GT_EQUALS, OPERATOR_LT_EQUALS } from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type { JqlData } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import type { DateTimeValue, TypeConfig } from '../../common/types.tsx';
import messages from './messages.tsx';
import { IS_YYYY_MM_DD_REGEX } from './time-functions.tsx';

export const BETWEEN_DATE_TIME_CONFIG: TypeConfig = {
	type: 'between_date_time',
	componentType: 'between_date_time',
	label: 'labelBetween',
	hasInput: true,
	getErrorMessage: (intl: IntlShape, value?: DateTimeValue): string | undefined => {
		if (value?.value1 && value?.value2 && parseISO(value?.value1) > parseISO(value?.value2))
			return intl.formatMessage(messages.validationMessageBeforeGreaterThanAfter);
		if (
			value &&
			((value?.value1 !== '' && !isValidDate(parseISO(value?.value1))) ||
				(value?.value2 !== '' && !isValidDate(parseISO(value?.value2))))
		)
			return intl.formatMessage(messages.validationMessageInvalidDateFormat);
		return undefined;
	},
	getFormattedValue: (intl: IntlShape, value?: DateTimeValue) => {
		if (value) {
			if (value.value1 && value.value2) {
				const parsedValue1 = parseISO(value.value1);
				const parsedValue2 = parseISO(value.value2);
				if (isValidDate(parsedValue1) && isValidDate(parsedValue2)) {
					return `${intl.formatDate(parsedValue1, {
						dateStyle: 'short',
						timeStyle: 'short',
					})} - ${intl.formatDate(parsedValue2, {
						dateStyle: 'short',
						timeStyle: 'short',
					})}`;
				}
			} else if (
				(value.value1 && isValidDate(parseISO(value.value1))) ||
				(value.value2 && isValidDate(parseISO(value.value2)))
			) {
				const valueToformat = value.value1 || value.value2;
				return intl.formatDate(parseISO(valueToformat), {
					dateStyle: 'short',
					timeStyle: 'short',
				});
			}
		}
		return '';
	},
	getJqlData: ({ value1, value2 }: Partial<DateTimeValue> = {}) => {
		const formatValue = (value: string) => {
			const parsedValue = parseISO(value);
			return isValidDate(parsedValue) ? format(parsedValue, 'yyyy-MM-dd HH:mm') : value;
		};
		return {
			primaryClause: value1
				? {
						operator: OPERATOR_GT_EQUALS,
						operand: formatValue(value1),
					}
				: undefined,
			secondaryClause: value2
				? {
						operator: OPERATOR_LT_EQUALS,
						operand: formatValue(value2),
					}
				: undefined,
		};
	},
	getValueFromJqlData: (jqlData?: JqlData): DateTimeValue | undefined => {
		if (jqlData !== undefined) {
			const { primaryClause, secondaryClause } = jqlData;
			if (primaryClause === undefined && secondaryClause === undefined) {
				// Do not match type if both clauses are not present
				return undefined;
			}
			if (primaryClause !== undefined || secondaryClause !== undefined) {
				const returnValue = { value1: '', value2: '' };
				[primaryClause, secondaryClause].forEach((clause) => {
					if (clause) {
						const operatorValue = clause.operator ? clause.operator : '';
						const value = clause.operand !== undefined ? clause.operand : '';
						switch (operatorValue) {
							case OPERATOR_GT_EQUALS: {
								if (value.match(IS_YYYY_MM_DD_REGEX)) {
									returnValue.value1 = value;
								}
								break;
							}
							case OPERATOR_LT_EQUALS: {
								if (value.match(IS_YYYY_MM_DD_REGEX)) {
									returnValue.value2 = value;
								}
								break;
							}

							default:
								break;
						}
					}
				});
				if (returnValue.value1 || returnValue.value2) {
					return returnValue;
				}
			}
			return undefined;
		}
		return undefined;
	},
};
